import { DecorationSet } from '@codemirror/view';
import { Alert } from '@mui/joy';

import { useMemo } from 'react';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ExampleSchema, RunSchema } from '@/types/schema';

import { MinimalCodeContext } from '../utils/MinimalCodeContext';
import { RunDisplaySchema } from '../utils/types';
import Attachments from './Attachments';
import { RunDetailsIO } from './RunDetailsIO';
import { RunDetailsLLM } from './RunDetailsLLM';

export function RunInputsAndOutputs(props: {
  run: RunDisplaySchema;
  onEditInputs?: (inputs: RunSchema['inputs']) => void;
  onEditOutputs?: (outputs: RunSchema['outputs']) => void;
  showExpandAll?: boolean;
  inputsLoading?: boolean;
  outputsLoading?: boolean;
  errorLoading?: boolean;
  showSeparateAttachments?: boolean;
  isRunLoading?: boolean;
  inputSchema?: Record<string, unknown>;
  outputSchema?: Record<string, unknown>;
  inputsRef?: React.RefObject<HTMLDivElement>;
  outputsRef?: React.RefObject<HTMLDivElement>;
  prevRun?: RunSchema;
  example?: ExampleSchema;
  forceRawRender?: 'raw' | 'rendered';
  getDiffValues?: (type: 'inputs' | 'outputs') =>
    | {
        decoration: DecorationSet;
        generatedCode: string | undefined;
      }
    | undefined;
  variant?:
    | 'run'
    | [
        'plain',
        {
          input:
            | { type: 'key-heuristic' }
            | { type: 'key'; value: string }
            | { type: 'key'; value: string[] };
          output:
            | { type: 'key-heuristic' }
            | { type: 'key'; value: string }
            | { type: 'key'; value: string[] };
        }
      ]
    | 'example';
}) {
  const minimalCodeCtxValue = useMemo(() => ({ minimalCode: true }), []);

  return (
    <MinimalCodeContext.Provider value={minimalCodeCtxValue}>
      {props.showSeparateAttachments && (
        <Attachments
          attachments={Object.entries(props.example?.attachment_urls ?? {}).map(
            ([name, urls]) => ({
              name: name.startsWith('attachment.') ? name.slice(11) : name,
              url: urls.presigned_url,
            })
          )}
        />
      )}
      {props.run.run_type === 'llm' ? (
        <ErrorBoundary
          key={props.run?.id}
          fallback={(error) => (
            <>
              <Alert color="danger" variant="outlined" sx={{ marginBottom: 2 }}>
                <div className="flex flex-col gap-1.5">
                  <div>
                    Failed to render run details, showing provisional
                    input/output:
                  </div>
                  <pre>{error?.toString()}</pre>
                </div>
              </Alert>

              {props.run && (
                <RunDetailsIO
                  run={props.run}
                  prevRun={props.prevRun}
                  variant={props.variant}
                  exampleOutput={props.example?.outputs}
                  forceRawRender={props.forceRawRender}
                  getDiffValues={props.getDiffValues}
                  onEditInputs={props.onEditInputs}
                  onEditOutputs={props.onEditOutputs}
                  inputsRef={props.inputsRef}
                  outputsRef={props.outputsRef}
                  inputSchema={props.inputSchema}
                  outputSchema={props.outputSchema}
                  inputsLoading={props.inputsLoading}
                  outputsLoading={props.outputsLoading}
                  errorsLoading={props.errorLoading}
                  showExpandAll={props.showExpandAll}
                />
              )}
            </>
          )}
        >
          <RunDetailsLLM
            run={props.run}
            prevRun={props.prevRun}
            variant={
              Array.isArray(props.variant) ? props.variant[0] : props.variant
            }
            exampleOutput={props.example?.outputs}
            getDiffValues={props.getDiffValues}
            forceRawRender={props.forceRawRender}
            onEditInputs={props.onEditInputs}
            onEditOutputs={props.onEditOutputs}
            inputsRef={props.inputsRef}
            outputsRef={props.outputsRef}
            inputSchema={props.inputSchema}
            outputSchema={props.outputSchema}
            inputsLoading={props.inputsLoading}
            outputsLoading={props.outputsLoading}
            errorLoading={props.errorLoading}
            showExpandAll={props.showExpandAll}
          />
        </ErrorBoundary>
      ) : (
        <RunDetailsIO
          run={props.run}
          prevRun={props.prevRun}
          variant={props.variant}
          exampleOutput={props.example?.outputs}
          getDiffValues={props.getDiffValues}
          forceRawRender={props.forceRawRender}
          onEditInputs={props.onEditInputs}
          onEditOutputs={props.onEditOutputs}
          inputsRef={props.inputsRef}
          outputsRef={props.outputsRef}
          inputSchema={props.inputSchema}
          outputSchema={props.outputSchema}
          inputsLoading={props.inputsLoading}
          outputsLoading={props.outputsLoading}
          errorsLoading={props.errorLoading}
          showExpandAll={props.showExpandAll}
        />
      )}
    </MinimalCodeContext.Provider>
  );
}
